/**
 * Tracks connectivity and drains the offline sell queue (offline-queue.ts)
 * the moment the browser comes back online — plus a 15s poll while online,
 * since a browser's `online` event fires on "has a network interface",
 * not "can actually reach the API" (a captive portal or a flaky router
 * both look "online" to the browser but aren't).
 */
import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { pendingCount, queuedBillIds, replayQueueForBill } from './offline-queue';
import { listQueuedOps } from './offline-db';
import { listFailedOfflineBills, listOpenOfflineBills, replayOfflineBills } from './offline-bill';

const POLL_MS = 15_000;

export function useOfflineSync() {
  const queryClient = useQueryClient();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  /** FAILED items across both queues (features/offline-sync's review list) — a real server rejection, not just "still waiting for a connection". */
  const [failed, setFailed] = useState(0);

  const refreshPending = useCallback(() => {
    void Promise.all([pendingCount(), listOpenOfflineBills()]).then(([queued, offlineBills]) =>
      setPending(queued + offlineBills.length),
    );
    void Promise.all([listQueuedOps(), listFailedOfflineBills()]).then(([queuedOps, failedBills]) =>
      setFailed(queuedOps.filter((o) => o.status === 'FAILED').length + failedBills.length),
    );
  }, []);

  const syncNow = useCallback(async () => {
    if (!navigator.onLine) return;
    let syncedAny = false;

    const billIds = await queuedBillIds();
    for (const billId of billIds) {
      const result = await replayQueueForBill(billId);
      if (result) {
        syncedAny = true;
        queryClient.setQueryData(['bill', billId], result);
      }
    }

    const { syncedAny: offlineBillsSynced } = await replayOfflineBills();
    if (offlineBillsSynced) syncedAny = true;

    if (syncedAny) toast.success('Offline items synced');
    refreshPending();
  }, [queryClient, refreshPending]);

  useEffect(() => {
    refreshPending();
    const onOnline = () => {
      setIsOnline(true);
      void syncNow();
    };
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);

    const timer = setInterval(() => {
      refreshPending();
      if (navigator.onLine) void syncNow();
    }, POLL_MS);

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isOnline, pending, failed, syncNow, refreshPending };
}
